import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { ProductThumbnail } from "../components/ProductThumbnail";
import { BatchReconciliation } from "../components/BatchReconciliation";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { Plus, Package, Boxes, DollarSign, Trash2, ChevronDown, ChevronUp, TrendingUp, AlertCircle, Pencil, Search, CheckCircle, RefreshCw } from "lucide-react";

const MerchCostsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [batches, setBatches] = useState([]);
  const [internalProducts, setInternalProducts] = useState([]);  // Changed from merchProducts
  const [selectedProduct, setSelectedProduct] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [addCostDialogOpen, setAddCostDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [expandedBatches, setExpandedBatches] = useState({});
  const [sectionsCollapsed, setSectionsCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem("merchCosts.sectionsCollapsed");
      return saved !== null ? JSON.parse(saved) : { inactive: true };
    } catch {
      return { inactive: true };
    }
  });
  const toggleSection = (key) => {
    setSectionsCollapsed(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem("merchCosts.sectionsCollapsed", JSON.stringify(next)); } catch (e) { /* ignore */ }
      return next;
    });
  };

  // New batch form
  const [batchForm, setBatchForm] = useState({
    internal_product_id: "",  // Changed from merch_product_id
    batch_number: 1,
    total_pieces: 0,
    notes: "",
    sale_price: 0,
    payment_method_fee: 4.245,
    shipping_handling_fee: 30,
    sales_start_date: "",
    costs: [{ description: "", amount: 0, date: format(new Date(), "yyyy-MM-dd") }]
  });

  // Edit batch form
  const [editForm, setEditForm] = useState({
    total_pieces: 0,
    batch_number: 1,
    notes: "",
    sale_price: 0,
    payment_method_fee: 4.245,
    shipping_handling_fee: 30,
    sales_start_date: "",
    costs: []
  });

  // Debug state
  const [debugDialogOpen, setDebugDialogOpen] = useState(false);
  const [debugData, setDebugData] = useState(null);
  const [loadingDebug, setLoadingDebug] = useState(false);

  // Preview SKUs state
  const [previewSkus, setPreviewSkus] = useState([]);
  const [loadingSkus, setLoadingSkus] = useState(false);

  // Add cost form
  const [costForm, setCostForm] = useState({
    description: "",
    amount: 0,
    date: format(new Date(), "yyyy-MM-dd")
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount || 0);
  };

  // Redondea hacia arriba al múltiplo de 0.05 más cercano
  const roundUpToNearest05 = (value) => {
    return Math.ceil(value * 20) / 20;
  };

  // Calcula el costo final usando la fórmula correcta
  const calculateFinalCost = (costBase, salePrice, feePercent, shippingFee) => {
    // Comisión = precio_venta × (porcentaje / 100), redondeado al 0.05 hacia arriba
    const paymentFeeRaw = salePrice * (feePercent / 100);
    const paymentFeeAmount = roundUpToNearest05(paymentFeeRaw);
    // Costo final = costo base + comisión + shipping
    const finalCost = costBase + paymentFeeAmount + shippingFee;
    return { paymentFeeAmount, finalCost };
  };

  // Fetch SKUs when internal product changes
  const fetchSkusForProduct = async (productId) => {
    if (!productId) {
      setPreviewSkus([]);
      return;
    }
    setLoadingSkus(true);
    try {
      const res = await axios.get(`${API}/margin-categories/${productId}/skus`, getAuthHeaders());
      setPreviewSkus(res.data.skus || []);
    } catch (error) {
      console.error("Error fetching SKUs:", error);
      setPreviewSkus([]);
    } finally {
      setLoadingSkus(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedProduct]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [batchesRes, internalProductsRes] = await Promise.all([
        axios.get(`${API}/batches`, {
          params: selectedProduct !== "all" ? { internal_product_id: selectedProduct } : {},
          ...getAuthHeaders()
        }),
        axios.get(`${API}/margin-categories`, getAuthHeaders())  // Internal products
      ]);
      setBatches(batchesRes.data);
      setInternalProducts(internalProductsRes.data);
    } catch (error) {
      toast.error("Error al cargar datos");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBatch = async (e) => {
    e.preventDefault();
    
    if (!batchForm.internal_product_id) {
      toast.error("Selecciona un producto interno");
      return;
    }
    
    if (batchForm.total_pieces <= 0) {
      toast.error("Las piezas deben ser mayor a 0");
      return;
    }

    const validCosts = batchForm.costs.filter(c => c.description && c.amount > 0);
    
    try {
      await axios.post(`${API}/batches`, {
        internal_product_id: batchForm.internal_product_id,
        batch_number: parseInt(batchForm.batch_number) || 1,
        total_pieces: parseInt(batchForm.total_pieces) || 0,
        notes: batchForm.notes,
        sale_price: batchForm.sale_price === '' ? 0 : parseFloat(batchForm.sale_price),
        payment_method_fee: batchForm.payment_method_fee === '' ? 4.245 : parseFloat(batchForm.payment_method_fee),
        shipping_handling_fee: batchForm.shipping_handling_fee === '' ? 30 : parseFloat(batchForm.shipping_handling_fee),
        sales_start_date: batchForm.sales_start_date || null,
        costs: validCosts
      }, getAuthHeaders());
      
      toast.success("Lote creado correctamente");
      setDialogOpen(false);
      setBatchForm({
        internal_product_id: "",
        batch_number: 1,
        total_pieces: 0,
        notes: "",
        sale_price: 0,
        payment_method_fee: 4.245,
        shipping_handling_fee: 30,
        sales_start_date: "",
        costs: [{ description: "", amount: 0, date: format(new Date(), "yyyy-MM-dd") }]
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear lote");
    }
  };

  const handleAddCost = async (e) => {
    e.preventDefault();
    
    if (!costForm.description || costForm.amount <= 0) {
      toast.error("Completa los campos correctamente");
      return;
    }

    try {
      await axios.post(`${API}/batches/${selectedBatch.id}/add-cost`, costForm, getAuthHeaders());
      toast.success("Costo agregado");
      setAddCostDialogOpen(false);
      setCostForm({ description: "", amount: 0, date: format(new Date(), "yyyy-MM-dd") });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al agregar costo");
    }
  };

  const handleActivateBatch = async (batchId) => {
    try {
      await axios.post(`${API}/batches/${batchId}/activate`, {}, getAuthHeaders());
      toast.success("Lote activado");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al activar lote");
    }
  };

  const handleDeleteBatch = async (batchId) => {
    if (!window.confirm("¿Estás seguro de eliminar este lote?")) return;
    
    try {
      await axios.delete(`${API}/batches/${batchId}`, getAuthHeaders());
      toast.success("Lote eliminado");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar lote");
    }
  };

  const handleCompleteBatch = async (batch) => {
    const msg = `¿Marcar Lote #${batch.batch_number} de "${batch.internal_product_name || batch.merch_product_name}" como completado?\n\nPiezas vendidas: ${batch.pieces_sold || 0}/${batch.total_pieces}\n\nLas ventas futuras se asignarán al siguiente lote.`;
    if (!window.confirm(msg)) return;
    
    try {
      await axios.post(`${API}/batches/${batch.id}/complete`, null, getAuthHeaders());
      toast.success(`Lote #${batch.batch_number} marcado como completado`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al completar lote");
    }
  };

  const handleToggleRestocked = async (batch) => {
    try {
      const response = await axios.post(`${API}/batches/${batch.id}/toggle-restocked`, null, getAuthHeaders());
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cambiar estado de re-orden");
    }
  };

  const [resyncing, setResyncing] = useState(false);
  const handleResyncSkus = async () => {
    if (!window.confirm(
      "Esto va a resincronizar los SKUs de todos los lotes con lo que hoy tiene cada Producto Interno " +
      "en sus productos importados asignados. Se usa para que los lotes tomen en cuenta ventas históricas " +
      "cuando se creó el lote DESPUÉS de empezar a vender.\n\n¿Continuar?"
    )) return;
    setResyncing(true);
    try {
      const r = await axios.post(`${API}/admin/batches-resync-skus?only_active=false`, null, getAuthHeaders());
      const d = r.data || {};
      toast.success(
        `${d.updated || 0} lotes actualizados, ${d.unchanged || 0} sin cambios`
      );
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al recalcular lotes");
    } finally {
      setResyncing(false);
    }
  };

  // Open edit dialog with batch data
  const openEditDialog = (batch) => {
    setSelectedBatch(batch);
    setEditForm({
      total_pieces: batch.total_pieces,
      batch_number: batch.batch_number,
      notes: batch.notes || "",
      sale_price: batch.sale_price ?? 0,
      payment_method_fee: batch.payment_method_fee ?? 4.245,
      shipping_handling_fee: batch.shipping_handling_fee ?? 30,
      sales_start_date: batch.sales_start_date || "",
      costs: batch.costs?.map(c => ({ ...c })) || []
    });
    setEditDialogOpen(true);
  };

  // Handle batch update
  const handleUpdateBatch = async (e) => {
    e.preventDefault();
    
    if (editForm.total_pieces <= 0) {
      toast.error("Las piezas deben ser mayor a 0");
      return;
    }

    try {
      await axios.put(`${API}/batches/${selectedBatch.id}`, {
        ...editForm,
        batch_number: parseInt(editForm.batch_number) || 1,
        total_pieces: parseInt(editForm.total_pieces) || 0,
        sale_price: editForm.sale_price === '' ? 0 : parseFloat(editForm.sale_price),
        payment_method_fee: editForm.payment_method_fee === '' ? 4.245 : parseFloat(editForm.payment_method_fee),
        shipping_handling_fee: editForm.shipping_handling_fee === '' ? 30 : parseFloat(editForm.shipping_handling_fee),
        sales_start_date: editForm.sales_start_date || null
      }, getAuthHeaders());
      toast.success("Lote actualizado");
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar lote");
    }
  };

  // Debug batch sales
  const handleDebugBatch = async (batchId) => {
    setLoadingDebug(true);
    setDebugDialogOpen(true);
    try {
      const res = await axios.get(`${API}/admin/debug-batch-sales/${batchId}`, getAuthHeaders());
      setDebugData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al obtener debug");
      setDebugDialogOpen(false);
    } finally {
      setLoadingDebug(false);
    }
  };

  // Add cost to edit form
  const addEditCost = () => {
    setEditForm({
      ...editForm,
      costs: [...editForm.costs, { description: "", amount: 0, date: format(new Date(), "yyyy-MM-dd") }]
    });
  };

  // Update cost in edit form
  const updateEditCost = (index, field, value) => {
    const newCosts = [...editForm.costs];
    newCosts[index][field] = field === "amount" ? (value === '' ? '' : parseFloat(value)) : value;
    setEditForm({ ...editForm, costs: newCosts });
  };

  // Remove cost from edit form
  const removeEditCost = (index) => {
    if (editForm.costs.length > 0) {
      setEditForm({
        ...editForm,
        costs: editForm.costs.filter((_, i) => i !== index)
      });
    }
  };

  const addCostField = () => {
    setBatchForm({
      ...batchForm,
      costs: [...batchForm.costs, { description: "", amount: 0, date: format(new Date(), "yyyy-MM-dd") }]
    });
  };

  const updateCostField = (index, field, value) => {
    const newCosts = [...batchForm.costs];
    newCosts[index][field] = field === "amount" ? (value === '' ? '' : parseFloat(value)) : value;
    setBatchForm({ ...batchForm, costs: newCosts });
  };

  const removeCostField = (index) => {
    if (batchForm.costs.length > 1) {
      setBatchForm({
        ...batchForm,
        costs: batchForm.costs.filter((_, i) => i !== index)
      });
    }
  };

  const toggleExpand = (batchId) => {
    setExpandedBatches(prev => ({ ...prev, [batchId]: !prev[batchId] }));
  };

  // Calculate next batch number for selected product
  const getNextBatchNumber = (internalProductId) => {
    const productBatches = batches.filter(b => 
      (b.internal_product_id || b.merch_product_id || b.product_id) === internalProductId
    );
    return productBatches.length > 0 ? Math.max(...productBatches.map(b => b.batch_number)) + 1 : 1;
  };

  // Bucket batches by their state. A batch falls into exactly one of:
  //   - inactive   (gray)   → final state: sold-out/completed. Trumps everything.
  //   - restocked  (purple) → reposition ordered, not yet inactive
  //   - recovered  (green)  → fully recovered, awaiting re-order
  //   - active     (amber)  → still has pieces to sell and not recovered
  const restockedBatches = [];
  const recoveredBatches = [];
  const inactiveBatches = [];
  const activeBatches = [];

  for (const batch of batches) {
    const soldOut = batch.status === "inactive" ||
                    batch.manually_completed ||
                    (batch.pieces_sold >= batch.total_pieces && batch.total_pieces > 0);
    if (soldOut) {
      inactiveBatches.push(batch);
    } else if (batch.restocked) {
      restockedBatches.push(batch);
    } else if (batch.is_fully_recovered) {
      recoveredBatches.push(batch);
    } else {
      activeBatches.push(batch);
    }
  }

  // Sort all lists alphabetically by product name, then by batch number
  const sortBatches = (batchList) => {
    return [...batchList].sort((a, b) => {
      const nameA = (a.internal_product_name || a.merch_product_name || a.product_name || "").toLowerCase();
      const nameB = (b.internal_product_name || b.merch_product_name || b.product_name || "").toLowerCase();
      if (nameA !== nameB) return nameA.localeCompare(nameB);
      return (a.batch_number || 1) - (b.batch_number || 1);
    });
  };

  const sortedActiveBatches = sortBatches(activeBatches);
  const sortedRecoveredBatches = sortBatches(recoveredBatches);
  const sortedRestockedBatches = sortBatches(restockedBatches);
  const sortedInactiveBatches = sortBatches(inactiveBatches);

  if (loading && batches.length === 0) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner w-8 h-8 border-4 border-[#F59E0B] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="merch-costs-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#4A4A4A] font-['Manrope']">Costos y Lotes</h1>
            <p className="text-[#6B7280]">Gestiona la inversión por producto y lote de producción</p>
          </div>
          
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              className="border-[#3B82F6] text-[#3B82F6] hover:bg-[#3B82F6]/10"
              onClick={handleResyncSkus}
              disabled={resyncing}
              title="Actualiza los SKUs guardados en cada lote con los del Producto Interno actual, incluyendo ventas históricas anteriores a la fecha en que creaste el lote"
              data-testid="resync-batches-skus-button"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${resyncing ? "animate-spin" : ""}`} />
              {resyncing ? "Recalculando…" : "Recalcular con ventas históricas"}
            </Button>

            <Select value={selectedProduct} onValueChange={setSelectedProduct}>
              <SelectTrigger className="w-[200px] bg-white border-[#FECDD3]">
                <Package className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrar por producto" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Todos los productos</SelectItem>
                {internalProducts.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#F59E0B] hover:bg-[#D97706] text-white" data-testid="new-batch-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Lote
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-[#4A4A4A]">Crear Nuevo Lote de Producción</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateBatch} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Producto Interno</Label>
                      <Select 
                        value={batchForm.internal_product_id} 
                        onValueChange={(v) => {
                          setBatchForm({ 
                            ...batchForm, 
                            internal_product_id: v,
                            batch_number: getNextBatchNumber(v)
                          });
                          fetchSkusForProduct(v);
                        }}
                      >
                        <SelectTrigger className="bg-white border-[#FECDD3]">
                          <SelectValue placeholder="Seleccionar producto" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {internalProducts.map(p => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {internalProducts.length === 0 && (
                        <p className="text-xs text-[#EF4444]">
                          No hay productos internos. Créalos primero en "Productos".
                        </p>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Número de Lote</Label>
                      <Input 
                        type="number"
                        value={batchForm.batch_number}
                        onChange={(e) => setBatchForm({ ...batchForm, batch_number: e.target.value === '' ? '' : parseInt(e.target.value) })}
                        className="bg-white border-[#FECDD3]"
                      />
                    </div>
                  </div>

                  {/* SKUs Preview - shows inherited SKUs from assigned products */}
                  {batchForm.internal_product_id && (
                    <div className="p-3 bg-[#8B5CF6]/10 rounded-lg border border-[#8B5CF6]/30">
                      <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                        <Package className="w-4 h-4 text-[#8B5CF6]" />
                        SKUs Heredados Automáticamente
                      </Label>
                      {loadingSkus ? (
                        <p className="text-xs text-[#6B7280]">Cargando SKUs...</p>
                      ) : previewSkus.length > 0 ? (
                        <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                          {previewSkus.map((sku, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs bg-white">
                              {sku}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-[#EF4444]">
                          No hay SKUs asignados. Asigna productos importados a este producto interno en la pestaña "Productos".
                        </p>
                      )}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Total de Piezas</Label>
                    <Input 
                      type="number"
                      value={batchForm.total_pieces}
                      onChange={(e) => setBatchForm({ ...batchForm, total_pieces: e.target.value === '' ? '' : parseInt(e.target.value) })}
                      placeholder="Ej: 300"
                      className="bg-white border-[#FECDD3]"
                    />
                  </div>

                  {/* Sales Start Date - for historical sales tracking */}
                  <div className="space-y-2 p-3 bg-[#3B82F6]/10 rounded-lg border border-[#3B82F6]/30">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-[#3B82F6]" />
                      Fecha de Inicio de Ventas (Opcional)
                    </Label>
                    <Input 
                      type="date"
                      value={batchForm.sales_start_date}
                      onChange={(e) => setBatchForm({ ...batchForm, sales_start_date: e.target.value })}
                      className="bg-white border-[#BFDBFE]"
                      data-testid="sales-start-date-input"
                    />
                    <p className="text-xs text-[#6B7280]">
                      Define desde qué fecha contar las ventas para este lote. Útil para trackear ventas históricas de productos que ya se vendían antes de crear el lote.
                    </p>
                  </div>

                  {/* Pricing & Fee Configuration */}
                  <div className="space-y-3 p-3 bg-[#F59E0B]/10 rounded-lg border border-[#F59E0B]/30">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Precio de Venta por Pieza (MXN)</Label>
                      <Input 
                        type="number"
                        step="0.01"
                        value={batchForm.sale_price}
                        onChange={(e) => setBatchForm({ ...batchForm, sale_price: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                        placeholder="Ej: 500"
                        className="bg-white border-[#FECDD3]"
                        data-testid="sale-price-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm">Comisión Medio de Pago (%)</Label>
                        <Input 
                          type="number"
                          step="0.001"
                          value={batchForm.payment_method_fee}
                          onChange={(e) => setBatchForm({ ...batchForm, payment_method_fee: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                          placeholder="4.245"
                          className="bg-white border-[#FECDD3]"
                          data-testid="payment-method-fee-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm">Shipping & Handling (MXN)</Label>
                        <Input 
                          type="number"
                          step="0.01"
                          value={batchForm.shipping_handling_fee}
                          onChange={(e) => setBatchForm({ ...batchForm, shipping_handling_fee: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                          placeholder="30"
                          className="bg-white border-[#FECDD3]"
                          data-testid="shipping-handling-fee-input"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Costos de Producción</Label>
                      <Button type="button" variant="ghost" size="sm" onClick={addCostField} className="text-[#F59E0B]">
                        <Plus className="w-4 h-4 mr-1" /> Agregar costo
                      </Button>
                    </div>
                    
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {batchForm.costs.map((cost, index) => (
                        <div key={index} className="flex gap-2 items-center p-2 bg-[#FEE2E2]/30 rounded">
                          <Input 
                            placeholder="Descripción (ej: Muestra)"
                            value={cost.description}
                            onChange={(e) => updateCostField(index, "description", e.target.value)}
                            className="flex-1 bg-white border-[#FECDD3]"
                          />
                          <Input 
                            type="number"
                            placeholder="Monto"
                            value={cost.amount || ""}
                            onChange={(e) => updateCostField(index, "amount", e.target.value)}
                            className="w-32 bg-white border-[#FECDD3]"
                          />
                          <Input 
                            type="date"
                            value={cost.date}
                            onChange={(e) => updateCostField(index, "date", e.target.value)}
                            className="w-40 bg-white border-[#FECDD3]"
                          />
                          {batchForm.costs.length > 1 && (
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeCostField(index)} className="text-[#EF4444]">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    <div className="text-right text-sm text-[#6B7280] space-y-1 pt-2 border-t">
                      <div>Total Inversión: {formatCurrency(batchForm.costs.reduce((sum, c) => sum + (c.amount || 0), 0))}</div>
                      {batchForm.total_pieces > 0 && (
                        (() => {
                          const costBase = batchForm.costs.reduce((sum, c) => sum + (c.amount || 0), 0) / batchForm.total_pieces;
                          const salePrice = batchForm.sale_price === '' ? 0 : (batchForm.sale_price || 0);
                          const feePercent = batchForm.payment_method_fee === '' ? 4.245 : (batchForm.payment_method_fee ?? 4.245);
                          const shippingFee = batchForm.shipping_handling_fee === '' ? 30 : (batchForm.shipping_handling_fee ?? 30);
                          const { paymentFeeAmount, finalCost } = calculateFinalCost(costBase, salePrice, feePercent, shippingFee);
                          
                          return (
                            <>
                              <div>Costo base unitario: {formatCurrency(costBase)}</div>
                              {salePrice > 0 && (
                                <div>
                                  Comisión ({feePercent}% de {formatCurrency(salePrice)}): {formatCurrency(paymentFeeAmount)}
                                </div>
                              )}
                              <div>Shipping & Handling: {formatCurrency(shippingFee)}</div>
                              <div className="font-semibold text-[#F59E0B] text-base" data-testid="final-cost-preview">
                                Costo Final por Pieza: {formatCurrency(finalCost)}
                              </div>
                            </>
                          );
                        })()
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Notas</Label>
                    <Textarea 
                      value={batchForm.notes}
                      onChange={(e) => setBatchForm({ ...batchForm, notes: e.target.value })}
                      placeholder="Notas adicionales..."
                      className="bg-white border-[#FECDD3]"
                    />
                  </div>

                  <Button type="submit" className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-white">
                    Crear Lote
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-white border-[#F59E0B]/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Boxes className="w-10 h-10 text-[#F59E0B]" />
                <div>
                  <p className="text-sm text-[#6B7280]">Total Invertido</p>
                  <p className="text-xl font-bold text-[#F59E0B]">
                    {formatCurrency(batches.reduce((sum, b) => sum + b.total_cost, 0))}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-[#10B981]/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-10 h-10 text-[#10B981]" />
                <div>
                  <p className="text-sm text-[#6B7280]">Recuperado</p>
                  <p className="text-xl font-bold text-[#10B981]">
                    {formatCurrency(batches.reduce((sum, b) => sum + (b.amount_recovered || 0), 0))}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-[#EF4444]/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-10 h-10 text-[#EF4444]" />
                <div>
                  <p className="text-sm text-[#6B7280]">Pendiente</p>
                  <p className="text-xl font-bold text-[#EF4444]">
                    {formatCurrency(batches.reduce((sum, b) => sum + (b.total_cost - (b.amount_recovered || 0)), 0))}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Batch rendering helper component */}
        {(() => {
          const renderBatchCard = (batch) => {
            const recoveryPercent = batch.total_cost > 0 
              ? Math.min(100, ((batch.amount_recovered || 0) / batch.total_cost) * 100) 
              : 0;
            const isExpanded = expandedBatches[batch.id];
            const isInactive = batch.status === "inactive" || batch.manually_completed || (batch.pieces_sold >= batch.total_pieces && batch.total_pieces > 0);
            
            // Calculate final cost locally if not from backend
            const costBase = batch.cost_per_piece || 0;
            const salePrice = batch.sale_price || 0;
            const feePercent = batch.payment_method_fee ?? 4.245;
            const shippingFee = batch.shipping_handling_fee ?? 30;
            const { paymentFeeAmount, finalCost } = calculateFinalCost(costBase, salePrice, feePercent, shippingFee);
            const displayFinalCost = batch.final_cost_per_piece || finalCost;

            // Card color follows the same priority as the section buckets:
            //   1. Inactive   (gray)   → final state
            //   2. Restocked  (purple)
            //   3. Recovered  (green)
            //   4. Active     (amber)
            let cardClass = 'border-[#E5E7EB]';
            if (isInactive) {
              cardClass = 'border-[#9CA3AF] bg-[#F3F4F6] opacity-70';
            } else if (batch.restocked) {
              cardClass = 'border-2 border-[#6366F1] bg-[#6366F1]/25 shadow-md shadow-[#6366F1]/20';
            } else if (batch.is_fully_recovered) {
              cardClass = 'border-2 border-[#10B981] bg-[#10B981]/25 shadow-md shadow-[#10B981]/20';
            } else if (batch.is_active) {
              cardClass = 'border-2 border-[#F59E0B] bg-[#F59E0B]/15';
            }

            return (
              <div 
                key={batch.id} 
                className={`border rounded-lg p-4 ${cardClass}`}
                data-testid={`batch-card-${batch.id}`}
              >
                {/* Row 1: Toggle + Thumbnail + Name + Lote # + Restocked badge */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2 min-w-0 flex-1">
                    <button onClick={() => toggleExpand(batch.id)} className="text-[#6B7280] hover:text-[#4A4A4A] mt-0.5 flex-shrink-0">
                      {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                    <ProductThumbnail
                      image_url={batch.image_url}
                      name={batch.internal_product_name || batch.merch_product_name || batch.product_name}
                      size="md"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-[#4A4A4A] break-words">{batch.internal_product_name || batch.merch_product_name || batch.product_name}</span>
                        <Badge variant="outline" className="text-[#6B7280] flex-shrink-0">Lote #{batch.batch_number}</Badge>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 2: Status badges */}
                <div className="flex items-center gap-1.5 flex-wrap mt-2 ml-7">
                  {isInactive ? (
                    <Badge className="bg-[#9CA3AF] text-white">Inactivo</Badge>
                  ) : (
                    <Badge className="bg-[#10B981] text-white">Activo</Badge>
                  )}
                  {batch.is_fully_recovered && (
                    <Badge className="bg-[#3B82F6] text-white">Recuperado</Badge>
                  )}
                  {batch.manually_completed && (
                    <Badge className="bg-[#6B7280] text-white">Completado</Badge>
                  )}
                  {batch.restocked && (
                    <Badge className="bg-[#6366F1] text-white" data-testid={`restocked-badge-${batch.id}`}>
                      Re-ordenado{batch.restocked_at ? ` · ${batch.restocked_at}` : ''}
                    </Badge>
                  )}
                  {batch.sales_start_date && (
                    <Badge variant="outline" className="text-[#3B82F6] border-[#3B82F6]" data-testid={`sales-start-badge-${batch.id}`}>
                      Ventas desde: {batch.sales_start_date}
                    </Badge>
                  )}
                  {batch.inherited_skus?.length > 0 && (
                    <Badge variant="outline" className="text-[#8B5CF6] border-[#8B5CF6]">
                      {batch.inherited_skus.length} SKUs
                    </Badge>
                  )}
                </div>

                {/* Row 3: Description (piezas, venta, costo) */}
                <div className="mt-2 ml-7 space-y-0.5">
                  <p className="text-sm text-[#6B7280]">
                    <span className="font-semibold text-[#4A4A4A]">{batch.pieces_sold || 0}/{batch.total_pieces}</span> piezas
                    <span className="mx-1.5">·</span>
                    Venta: <span className="text-[#4A4A4A]">{formatCurrency(salePrice)}</span>
                    <span className="mx-1.5">·</span>
                    <span className="text-[#F59E0B] font-medium" data-testid={`final-cost-${batch.id}`}>
                      Costo Final: {formatCurrency(displayFinalCost)}
                    </span>
                  </p>
                  {(() => {
                    const totalCost = batch.total_cost || 0;
                    const piecesSold = batch.pieces_sold || 0;
                    const amountRecovered = batch.amount_recovered || 0;
                    if (!salePrice || totalCost <= 0) return null;
                    const piecesToRecover = batch.pieces_to_recover ?? Math.ceil(totalCost / salePrice);
                    const remaining = Math.max(0, piecesToRecover - piecesSold);
                    const isRecovered = amountRecovered >= totalCost;
                    return (
                      <p className="text-xs text-[#6B7280]" data-testid={`pieces-to-recover-${batch.id}`}>
                        Para recuperar: <span className="font-semibold text-[#3B82F6]">{piecesToRecover} piezas</span>
                        {isRecovered ? (
                          <span className="text-[#10B981] font-medium"> ✓ recuperado</span>
                        ) : remaining > 0 && (
                          <span className="text-[#F59E0B]"> (faltan {remaining})</span>
                        )}
                        {batch.recovery_per_piece > 0 && (
                          <span className="block text-[11px] text-[#9CA3AF]" data-testid={`recovery-per-piece-${batch.id}`}>
                            Cada pieza aporta ~{formatCurrency(batch.recovery_per_piece)} (tienda: costo + margen merch · en línea: 100%)
                          </span>
                        )}
                      </p>
                    );
                  })()}
                </div>

                {/* Row 4: Inversión / Recuperado side-by-side */}
                <div className="mt-3 ml-7 grid grid-cols-2 gap-3">
                  <div className="bg-white/40 rounded p-2 border border-black/5">
                    <p className="text-xs text-[#6B7280]">Inversión</p>
                    <p className="font-bold text-[#F59E0B] text-base">{formatCurrency(batch.total_cost)}</p>
                  </div>
                  <div className="bg-white/40 rounded p-2 border border-black/5">
                    <p className="text-xs text-[#6B7280]">Recuperado</p>
                    <p className="font-bold text-[#10B981] text-base">{formatCurrency(batch.amount_recovered)}</p>
                  </div>
                </div>

                {/* Row 5: Recovery Progress */}
                <div className="mt-3 ml-7">
                  <div className="flex justify-between text-xs text-[#6B7280] mb-1">
                    <span>Recuperación de inversión</span>
                    <span>{recoveryPercent.toFixed(1)}%</span>
                  </div>
                  <Progress value={recoveryPercent} className="h-2" />
                </div>

                {!isInactive && <BatchReconciliation batch={batch} />}
                {/* Row 6: Action buttons */}
                <div className="mt-3 ml-7 flex flex-wrap gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleRestocked(batch)}
                    className={batch.restocked ? "text-[#6366F1] hover:bg-[#6366F1]/10" : "text-[#9CA3AF] hover:bg-[#6366F1]/10 hover:text-[#6366F1]"}
                    title={batch.restocked ? "Re-ordenado para restock — clic para quitar marca" : "Marcar como re-ordenado para restock"}
                    data-testid={`toggle-restocked-${batch.id}`}
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                  {!isInactive && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCompleteBatch(batch)}
                      className="text-[#10B981] hover:bg-[#10B981]/10"
                      title="Marcar como completado"
                      data-testid={`complete-batch-${batch.id}`}
                    >
                      <CheckCircle className="w-4 h-4" />
                    </Button>
                  )}
                  {!batch.is_active && !isInactive && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleActivateBatch(batch.id)}
                      className="text-[#F59E0B] hover:bg-[#F59E0B]/10"
                    >
                      Activar
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditDialog(batch)}
                    className="text-[#8B5CF6] hover:bg-[#8B5CF6]/10"
                    title="Editar lote"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDebugBatch(batch.id)}
                    className="text-[#F59E0B] hover:bg-[#F59E0B]/10"
                    title="Debug ventas"
                  >
                    <Search className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setSelectedBatch(batch); setAddCostDialogOpen(true); }}
                    className="text-[#3B82F6] hover:bg-[#3B82F6]/10"
                    title="Agregar costo"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const hasSales = (batch.pieces_sold || 0) > 0;
                      const msg = hasSales
                        ? `Este lote tiene ${batch.pieces_sold} ventas registradas. ¿Seguro que deseas eliminarlo?`
                        : "¿Eliminar este lote?";
                      if (confirm(msg)) handleDeleteBatch(batch.id);
                    }}
                    className="text-[#EF4444] hover:bg-[#EF4444]/10"
                    title="Eliminar lote"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-[#E5E7EB]">
                    {/* Cost Calculation Breakdown */}
                    <div className="mb-4 p-3 bg-[#F59E0B]/10 rounded-lg border border-[#F59E0B]/30">
                      <h4 className="text-sm font-medium text-[#4A4A4A] mb-3">Desglose de Costo Final por Pieza</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-[#6B7280]">Costo base (inversión ÷ piezas):</span>
                          <span className="font-mono text-[#4A4A4A]">{formatCurrency(costBase)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#6B7280]">Precio de venta:</span>
                          <span className="font-mono text-[#4A4A4A]">{formatCurrency(salePrice)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#6B7280]">Comisión medio de pago ({feePercent}%):</span>
                          <span className="font-mono text-[#4A4A4A]">
                            {formatCurrency(salePrice)} × {feePercent}% = {formatCurrency(paymentFeeAmount)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#6B7280]">Shipping & Handling:</span>
                          <span className="font-mono text-[#4A4A4A]">{formatCurrency(shippingFee)}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-[#F59E0B]/30 font-semibold">
                          <span className="text-[#4A4A4A]">COSTO FINAL:</span>
                          <span className="font-mono text-[#F59E0B]">
                            {formatCurrency(costBase)} + {formatCurrency(paymentFeeAmount)} + {formatCurrency(shippingFee)} = {formatCurrency(displayFinalCost)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <h4 className="text-sm font-medium text-[#4A4A4A] mb-2">Desglose de Costos de Producción</h4>
                    <div className="space-y-1">
                      {batch.costs?.map((cost, index) => (
                        <div key={index} className="flex justify-between text-sm py-1 px-2 bg-[#F9FAFB] rounded">
                          <span className="text-[#6B7280]">{cost.description}</span>
                          <div className="flex gap-4">
                            <span className="text-[#9CA3AF]">{cost.date}</span>
                            <span className="text-[#4A4A4A] font-mono">{formatCurrency(cost.amount)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    {batch.notes && (
                      <p className="mt-2 text-sm text-[#6B7280] italic">"{batch.notes}"</p>
                    )}
                  </div>
                )}
              </div>
            );
          };

          return (
            <>
              {/* No batches at all */}
              {batches.length === 0 && (
                <Card className="bg-white border-[#FECDD3]">
                  <CardContent className="p-12 text-center">
                    <Boxes className="w-16 h-16 mx-auto text-[#6B7280]/30 mb-4" />
                    <h3 className="text-lg font-medium text-[#4A4A4A]">No hay lotes registrados</h3>
                    <p className="text-[#6B7280] mt-1">Crea tu primer lote de producción para comenzar a trackear costos</p>
                  </CardContent>
                </Card>
              )}

              {/* Active Batches Section */}
              {sortedActiveBatches.length > 0 && (
                <Card className="bg-white border-[#F59E0B]/30" data-testid="active-batches-section">
                  <CardHeader className="pb-2 cursor-pointer select-none" onClick={() => toggleSection('active')} data-testid="active-batches-toggle">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-[#4A4A4A] font-['Manrope'] flex items-center gap-2">
                        {sectionsCollapsed.active
                          ? <ChevronDown className="w-5 h-5 text-[#F59E0B]" />
                          : <ChevronUp className="w-5 h-5 text-[#F59E0B]" />}
                        <TrendingUp className="w-5 h-5 text-[#F59E0B]" />
                        Lotes Activos
                      </CardTitle>
                      <Badge className="bg-[#F59E0B] text-white">
                        {sortedActiveBatches.length} lote(s)
                      </Badge>
                    </div>
                    {!sectionsCollapsed.active && (
                      <CardDescription>
                        Lotes que aún tienen piezas por vender
                      </CardDescription>
                    )}
                  </CardHeader>
                  {!sectionsCollapsed.active && (
                    <CardContent className="space-y-3">
                      {sortedActiveBatches.map(batch => renderBatchCard(batch))}
                    </CardContent>
                  )}
                </Card>
              )}

              {/* Recovered Batches Section */}
              {sortedRecoveredBatches.length > 0 && (
                <Card className="bg-white border-[#10B981]/30" data-testid="recovered-batches-section">
                  <CardHeader className="pb-2 cursor-pointer select-none" onClick={() => toggleSection('recovered')} data-testid="recovered-batches-toggle">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-[#4A4A4A] font-['Manrope'] flex items-center gap-2">
                        {sectionsCollapsed.recovered
                          ? <ChevronDown className="w-5 h-5 text-[#10B981]" />
                          : <ChevronUp className="w-5 h-5 text-[#10B981]" />}
                        <CheckCircle className="w-5 h-5 text-[#10B981]" />
                        Lotes Recuperados — pendientes de re-ordenar
                      </CardTitle>
                      <Badge className="bg-[#10B981] text-white">
                        {sortedRecoveredBatches.length} lote(s)
                      </Badge>
                    </div>
                    {!sectionsCollapsed.recovered && (
                      <CardDescription>
                        Ya recuperaron la inversión. Marca el botón ↻ cuando ya hayas pedido reposición al proveedor.
                      </CardDescription>
                    )}
                  </CardHeader>
                  {!sectionsCollapsed.recovered && (
                    <CardContent className="space-y-3">
                      {sortedRecoveredBatches.map(batch => renderBatchCard(batch))}
                    </CardContent>
                  )}
                </Card>
              )}

              {/* Restocked Batches Section */}
              {sortedRestockedBatches.length > 0 && (
                <Card className="bg-white border-[#6366F1]/30" data-testid="restocked-batches-section">
                  <CardHeader className="pb-2 cursor-pointer select-none" onClick={() => toggleSection('restocked')} data-testid="restocked-batches-toggle">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-[#4A4A4A] font-['Manrope'] flex items-center gap-2">
                        {sectionsCollapsed.restocked
                          ? <ChevronDown className="w-5 h-5 text-[#6366F1]" />
                          : <ChevronUp className="w-5 h-5 text-[#6366F1]" />}
                        <RefreshCw className="w-5 h-5 text-[#6366F1]" />
                        Lotes Re-ordenados
                      </CardTitle>
                      <Badge className="bg-[#6366F1] text-white">
                        {sortedRestockedBatches.length} lote(s)
                      </Badge>
                    </div>
                    {!sectionsCollapsed.restocked && (
                      <CardDescription>
                        Reposición ya pedida al proveedor. Cuando llegue la nueva mercancía, crea un nuevo lote.
                      </CardDescription>
                    )}
                  </CardHeader>
                  {!sectionsCollapsed.restocked && (
                    <CardContent className="space-y-3">
                      {sortedRestockedBatches.map(batch => renderBatchCard(batch))}
                    </CardContent>
                  )}
                </Card>
              )}

              {/* Inactive Batches Section — final state, collapsible, at the bottom */}
              {sortedInactiveBatches.length > 0 && (
                <Card className="bg-white border-[#9CA3AF]/30" data-testid="inactive-batches-section">
                  <CardHeader
                    className="pb-2 cursor-pointer select-none"
                    onClick={() => toggleSection('inactive')}
                    data-testid="inactive-batches-toggle"
                  >
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-[#6B7280] font-['Manrope'] flex items-center gap-2">
                        {sectionsCollapsed.inactive
                          ? <ChevronDown className="w-5 h-5 text-[#9CA3AF]" />
                          : <ChevronUp className="w-5 h-5 text-[#9CA3AF]" />}
                        <Package className="w-5 h-5 text-[#9CA3AF]" />
                        Lotes Inactivos
                      </CardTitle>
                      <Badge variant="outline" className="text-[#6B7280] border-[#9CA3AF]">
                        {sortedInactiveBatches.length} lote(s)
                      </Badge>
                    </div>
                    {!sectionsCollapsed.inactive && (
                      <CardDescription>
                        Estado final del lote: agotado/completado. Archivado para referencia histórica.
                      </CardDescription>
                    )}
                  </CardHeader>
                  {!sectionsCollapsed.inactive && (
                    <CardContent className="space-y-3">
                      {sortedInactiveBatches.map(batch => renderBatchCard(batch))}
                    </CardContent>
                  )}
                </Card>
              )}
            </>
          );
        })()}

        {/* Add Cost Dialog */}
        <Dialog open={addCostDialogOpen} onOpenChange={setAddCostDialogOpen}>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle className="text-[#4A4A4A]">
                Agregar Costo al Lote #{selectedBatch?.batch_number}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddCost} className="space-y-4">
              <div className="space-y-2">
                <Label>Descripción</Label>
                <Input 
                  value={costForm.description}
                  onChange={(e) => setCostForm({ ...costForm, description: e.target.value })}
                  placeholder="Ej: Envío, Importación, etc."
                  className="bg-white border-[#FECDD3]"
                />
              </div>
              <div className="space-y-2">
                <Label>Monto</Label>
                <Input 
                  type="number"
                  value={costForm.amount === 0 ? '' : costForm.amount}
                  onChange={(e) => setCostForm({ ...costForm, amount: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                  placeholder="0.00"
                  className="bg-white border-[#FECDD3]"
                />
              </div>
              <div className="space-y-2">
                <Label>Fecha</Label>
                <Input 
                  type="date"
                  value={costForm.date}
                  onChange={(e) => setCostForm({ ...costForm, date: e.target.value })}
                  className="bg-white border-[#FECDD3]"
                />
              </div>
              <Button type="submit" className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-white">
                Agregar Costo
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Batch Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[#4A4A4A]">
                Editar Lote #{selectedBatch?.batch_number} - {selectedBatch?.product_name}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateBatch} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Número de Lote</Label>
                  <Input 
                    type="number"
                    value={editForm.batch_number}
                    onChange={(e) => setEditForm({ ...editForm, batch_number: e.target.value === '' ? '' : parseInt(e.target.value) })}
                    className="bg-white border-[#FECDD3]"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Total de Piezas</Label>
                  <Input 
                    type="number"
                    value={editForm.total_pieces}
                    onChange={(e) => setEditForm({ ...editForm, total_pieces: e.target.value === '' ? '' : parseInt(e.target.value) })}
                    className="bg-white border-[#FECDD3]"
                  />
                </div>
              </div>

              {/* Sales Start Date - for historical sales tracking */}
              <div className="space-y-2 p-3 bg-[#3B82F6]/10 rounded-lg border border-[#3B82F6]/30">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-[#3B82F6]" />
                  Fecha de Inicio de Ventas
                </Label>
                <Input 
                  type="date"
                  value={editForm.sales_start_date}
                  onChange={(e) => setEditForm({ ...editForm, sales_start_date: e.target.value })}
                  className="bg-white border-[#BFDBFE]"
                  data-testid="edit-sales-start-date-input"
                />
                <p className="text-xs text-[#6B7280]">
                  Define desde qué fecha contar las ventas para este lote. Deja vacío para usar la fecha del primer costo.
                </p>
              </div>

              {/* Pricing & Fee Configuration */}
              <div className="space-y-3 p-3 bg-[#F59E0B]/10 rounded-lg border border-[#F59E0B]/30">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Precio de Venta por Pieza (MXN)</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={editForm.sale_price}
                    onChange={(e) => setEditForm({ ...editForm, sale_price: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                    placeholder="Ej: 500"
                    className="bg-white border-[#FECDD3]"
                    data-testid="edit-sale-price-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm">Comisión Medio de Pago (%)</Label>
                    <Input 
                      type="number"
                      step="0.001"
                      value={editForm.payment_method_fee}
                      onChange={(e) => setEditForm({ ...editForm, payment_method_fee: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                      placeholder="4.245"
                      className="bg-white border-[#FECDD3]"
                      data-testid="edit-payment-method-fee-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Shipping & Handling (MXN)</Label>
                    <Input 
                      type="number"
                      step="0.01"
                      value={editForm.shipping_handling_fee}
                      onChange={(e) => setEditForm({ ...editForm, shipping_handling_fee: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                      placeholder="30"
                      className="bg-white border-[#FECDD3]"
                      data-testid="edit-shipping-handling-fee-input"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Costos de Producción</Label>
                  <Button type="button" variant="ghost" size="sm" onClick={addEditCost} className="text-[#F59E0B]">
                    <Plus className="w-4 h-4 mr-1" /> Agregar costo
                  </Button>
                </div>
                
                <div className="space-y-2 max-h-[250px] overflow-y-auto">
                  {editForm.costs.map((cost, index) => (
                    <div key={index} className="flex gap-2 items-center p-2 bg-[#FEE2E2]/30 rounded">
                      <Input 
                        placeholder="Descripción"
                        value={cost.description}
                        onChange={(e) => updateEditCost(index, "description", e.target.value)}
                        className="flex-1 bg-white border-[#FECDD3]"
                      />
                      <Input 
                        type="number"
                        placeholder="Monto"
                        value={cost.amount || ""}
                        onChange={(e) => updateEditCost(index, "amount", e.target.value)}
                        className="w-32 bg-white border-[#FECDD3]"
                      />
                      <Input 
                        type="date"
                        value={cost.date || ""}
                        onChange={(e) => updateEditCost(index, "date", e.target.value)}
                        className="w-40 bg-white border-[#FECDD3]"
                      />
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => removeEditCost(index)} 
                        className="text-[#EF4444] hover:bg-[#EF4444]/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {editForm.costs.length === 0 && (
                    <p className="text-center py-4 text-[#6B7280]">No hay costos. Agrega al menos uno.</p>
                  )}
                </div>
                
                <div className="text-right text-sm text-[#6B7280] pt-2 border-t space-y-1">
                  <div>Total Inversión: {formatCurrency(editForm.costs.reduce((sum, c) => sum + (c.amount || 0), 0))}</div>
                  {editForm.total_pieces > 0 && (
                    (() => {
                      const costBase = editForm.costs.reduce((sum, c) => sum + (c.amount || 0), 0) / editForm.total_pieces;
                      const salePrice = editForm.sale_price === '' ? 0 : (editForm.sale_price || 0);
                      const feePercent = editForm.payment_method_fee === '' ? 4.245 : (editForm.payment_method_fee ?? 4.245);
                      const shippingFee = editForm.shipping_handling_fee === '' ? 30 : (editForm.shipping_handling_fee ?? 30);
                      const { paymentFeeAmount, finalCost } = calculateFinalCost(costBase, salePrice, feePercent, shippingFee);
                      
                      return (
                        <>
                          <div>Costo base unitario: {formatCurrency(costBase)}</div>
                          {salePrice > 0 && (
                            <div>
                              Comisión ({feePercent}% de {formatCurrency(salePrice)}): {formatCurrency(paymentFeeAmount)}
                            </div>
                          )}
                          <div>Shipping & Handling: {formatCurrency(shippingFee)}</div>
                          <div className="font-semibold text-[#F59E0B] text-base" data-testid="edit-final-cost-preview">
                            Costo Final por Pieza: {formatCurrency(finalCost)}
                          </div>
                        </>
                      );
                    })()
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notas</Label>
                <Textarea 
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  placeholder="Notas adicionales..."
                  className="bg-white border-[#FECDD3]"
                />
              </div>

              <div className="flex gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setEditDialogOpen(false)} 
                  className="flex-1 border-[#FECDD3]"
                >
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 bg-[#8B5CF6] hover:bg-[#7C3AED] text-white">
                  Guardar Cambios
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Debug Sales Dialog */}
        <Dialog open={debugDialogOpen} onOpenChange={setDebugDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[#F59E0B]">Debug: Ventas del Lote</DialogTitle>
            </DialogHeader>
            {loadingDebug ? (
              <div className="text-center py-8">Cargando...</div>
            ) : debugData ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 p-4 bg-[#FEF3C7] rounded-lg">
                  <div>
                    <p className="text-sm text-[#6B7280]">Producto</p>
                    <p className="font-semibold">{debugData.merch_product}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[#6B7280]">Fecha inicio ventas</p>
                    <p className="font-semibold">{debugData.batch_start_date}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[#6B7280]">SKUs buscados</p>
                    <p className="font-semibold">{debugData.sku_list?.join(", ") || "Ninguno"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[#6B7280]">Total encontrado</p>
                    <p className="font-semibold text-[#F59E0B]">{debugData.total_pieces} piezas | ${debugData.total_amount?.toFixed(2)}</p>
                  </div>
                </div>
                
                <div className="text-sm text-[#6B7280]">
                  Mostrando {debugData.sales?.length} de {debugData.total_sales_found} ventas encontradas
                </div>
                
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F3F4F6]">
                      <tr>
                        <th className="p-2 text-left">Fecha</th>
                        <th className="p-2 text-left">SKU</th>
                        <th className="p-2 text-right">Qty</th>
                        <th className="p-2 text-right">Total</th>
                        <th className="p-2 text-left">Match</th>
                      </tr>
                    </thead>
                    <tbody>
                      {debugData.sales?.map((sale, idx) => (
                        <tr key={idx} className="border-t hover:bg-[#FEF3C7]/30">
                          <td className="p-2">{sale.date}</td>
                          <td className="p-2 font-mono text-xs">{sale.sku}</td>
                          <td className="p-2 text-right">{sale.quantity}</td>
                          <td className="p-2 text-right">${sale.gross_total?.toFixed(2)}</td>
                          <td className="p-2 text-xs text-[#6B7280]">{sale.match_reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default MerchCostsPage;
